import { useCallback, useMemo } from 'react';

import Text from '@cartwhl/ui/base/Text';
import ChevronLeftIcon from '@cartwhl/ui/icons/ChevronLeftIcon';
import Button from '@cartwhl/ui/input/Button';
import Modal from '@cartwhl/ui/molecules/Modal';

function formatMotionModalTitle(
  batchMotionNumber: number,
  rawName: string,
): string {
  const padded = String(batchMotionNumber).padStart(2, '0');
  const base = rawName.trim() || 'motion';
  const withExt = base.toLowerCase().endsWith('.mp4') ? base : `${base}.mp4`;
  return `${padded}. ${withExt}`;
}

/** White + hairline border; matches batch preview modal controls. */
const OUTLINE_WHITE_BUTTON =
  '!bg-white dark:!bg-gray-90 !border-gray-30 dark:!border-gray-75 border hover:!bg-gray-15 dark:hover:!bg-gray-75';

export type BatchComicErrorNavigationJob = {
  id: string;
  name: string;
  batchMotionNumber: number;
};

type BatchComicJobErrorModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  jobTitle: string;
  batchMotionNumber: number;
  totalMotionsInBatch: number;
  errorMessage: string;
  navigation: {
    jobs: BatchComicErrorNavigationJob[];
    onSelectJobId: (jobId: string) => void;
  };
};

export default function BatchComicJobErrorModal({
  open,
  onOpenChange,
  jobId,
  jobTitle,
  batchMotionNumber,
  totalMotionsInBatch,
  errorMessage,
  navigation,
}: BatchComicJobErrorModalProps) {
  const ordered = navigation.jobs;

  const goAdjacent = useCallback(
    (delta: -1 | 1) => {
      if (ordered.length <= 1) return;
      const i = ordered.findIndex((j) => j.id === jobId);
      if (i < 0) return;
      const len = ordered.length;
      const next = (i + delta + len) % len;
      navigation.onSelectJobId(ordered[next]!.id);
    },
    [ordered, jobId, navigation],
  );

  const modalTitle = useMemo(
    () => (
      <div className="flex w-full min-w-0 items-center justify-between gap-3">
        <Text
          as="div"
          variant="studio/title/md"
          className="text-primary min-w-0 flex-1 truncate text-start text-[20px]/[20px]"
        >
          {formatMotionModalTitle(batchMotionNumber, jobTitle)}
        </Text>
        <div className="flex shrink-0 items-center gap-3">
          <Text
            as="span"
            variant="studio/body/md"
            className="text-gray-60 whitespace-nowrap"
          >
            {batchMotionNumber} of {totalMotionsInBatch}
          </Text>
          <Button
            type="button"
            variant="secondary"
            size="lg"
            className={`h-10 w-10 shrink-0 p-0 ${OUTLINE_WHITE_BUTTON}`}
            disabled={ordered.length <= 1}
            icon={<ChevronLeftIcon className="h-5 w-5" />}
            aria-label="Previous error"
            onClick={() => goAdjacent(-1)}
          />
          <Button
            type="button"
            variant="secondary"
            size="lg"
            className={`h-10 w-10 shrink-0 p-0 ${OUTLINE_WHITE_BUTTON}`}
            disabled={ordered.length <= 1}
            icon={<ChevronLeftIcon className="h-5 w-5 rotate-180" aria-hidden />}
            aria-label="Next error"
            onClick={() => goAdjacent(1)}
          />
        </div>
      </div>
    ),
    [
      batchMotionNumber,
      goAdjacent,
      jobTitle,
      ordered.length,
      totalMotionsInBatch,
    ],
  );

  const supportMail = 'support@getcartwheel.com';

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={modalTitle}
      maxWidth={960}
      fixedHeightContent={false}
      fullscreenOnMobile
      horizontalBleed
      verticalBleed
      titleClassName="!mb-3 !px-3 !pt-3 lg:!mb-4 lg:!px-4 lg:!pt-4"
    >
      <div className="flex flex-col gap-4 px-3 pb-3 lg:gap-5 lg:px-4 lg:pb-4">
        <div className="bg-red/10 dark:bg-red/20 rounded-xl px-6 py-4 lg:px-8 lg:py-4">
          <Text
            as="p"
            variant="studio/body/md"
            className="text-secondary mb-3 text-start"
          >
            Error details
          </Text>
          <Text
            as="p"
            variant="studio/body/medium/md"
            className="text-primary whitespace-pre-wrap wrap-break-word"
          >
            {errorMessage.trim() || 'No error message was provided.'}
          </Text>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-30 pt-4 dark:border-gray-75">
          <a
            href={`mailto:${supportMail}`}
            className="inline-flex no-underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button type="button" variant="secondary" size="lg">
              Contact support
            </Button>
          </a>
          <Button
            type="button"
            variant="primary"
            size="lg"
            onClick={() => onOpenChange(false)}
          >
            Done
          </Button>
        </div>
      </div>
    </Modal>
  );
}
